import { EditorStarterAsset } from '../../assets/assets';
import {
  getAssetDurationInSeconds,
  getAssetStartInSeconds,
} from '../../assets/utils';
import { getItemPlaybackRate } from '../../items/get-item-playback-rate';
import { EditorStarterItem } from '../../items/item-type';
import { TransitionItem } from '../../items/transition/transition-item-type';

interface CalculateFrozenFramesParams {
  transition: TransitionItem;
  sourceItem: EditorStarterItem;
  targetItem: EditorStarterItem;
  assets: Record<string, EditorStarterAsset>;
  fps: number;
  // 논리적 확장 정보 (UI에 반영되지 않고 재생 시에만 사용)
  sourceExtendedFrames?: number;
  targetExtendedFrames?: number;
}

interface FrozenFramesResult {
  sourceFrozenFrames: number;
  targetFrozenFrames: number;
}

/**
 * 트랜지션에 필요한 frozen frame 수를 계산합니다.
 *
 * 트랜지션이 클립 경계에서 시작할 때:
 * - sourceItem (앞쪽 클립): 트랜지션의 절반이 클립 끝을 초과하면 frozen frame 필요
 * - targetItem (뒤쪽 클립): 트랜지션의 절반이 클립 시작보다 앞으로 가면 frozen frame 필요
 */
export const calculateFrozenFrames = ({
  transition,
  sourceItem,
  targetItem,
  assets,
  fps,
  sourceExtendedFrames = 0,
  targetExtendedFrames = 0,
}: CalculateFrozenFramesParams): FrozenFramesResult => {
  // === Source Item (앞쪽 클립) frozen frame 계산 ===
  // 트랜지션이 source 아이템의 끝을 얼마나 초과하는지
  // 논리적 확장을 고려한 아이템 끝 (UI에는 반영되지 않지만 재생 시 사용)
  const sourceItemEnd =
    sourceItem.from + sourceItem.durationInFrames + sourceExtendedFrames;

  // source 아이템에서 트랜지션에 사용 가능한 프레임 수
  const sourceAvailableForTransition = sourceItemEnd - transition.from;

  // 에셋의 남은 프레임 수 계산 (원본 비디오 길이 기준)
  // GIF/WebP는 자동 루프되므로 항상 무한한 프레임 사용 가능
  let sourceAssetRemainingFrames = Infinity;
  if (
    sourceItem.type !== 'gif' &&
    sourceItem.type !== 'webp' &&
    'assetId' in sourceItem &&
    sourceItem.assetId
  ) {
    const asset = assets[sourceItem.assetId];
    if (asset) {
      const assetDuration = getAssetDurationInSeconds(asset);
      const assetStart = getAssetStartInSeconds(sourceItem) ?? 0;
      const playbackRate = getItemPlaybackRate(sourceItem);

      if (assetDuration !== null) {
        // 에셋에서 사용 가능한 총 프레임 수 (확장 포함)
        const totalAssetFrames = Math.floor(
          ((assetDuration - assetStart) * fps) / playbackRate,
        );
        // 트랜지션 시작 시점에서 에셋의 남은 프레임
        const framesSinceItemStart = transition.from - sourceItem.from;
        sourceAssetRemainingFrames = Math.max(
          0,
          totalAssetFrames - framesSinceItemStart,
        );
      }
    }
  }

  // 트랜지션에 필요한 프레임 (from부터 끝까지) vs 실제 사용 가능한 프레임
  const sourceNeededFrames = transition.durationInFrames;
  const sourceActuallyAvailable = Math.min(
    sourceAvailableForTransition,
    sourceAssetRemainingFrames,
  );
  const sourceFrozenFrames = Math.max(
    0,
    sourceNeededFrames - sourceActuallyAvailable,
  );

  // === Target Item (뒤쪽 클립) frozen frame 계산 ===
  // 트랜지션이 target 아이템의 시작보다 앞에서 시작하는 프레임 수
  // 논리적 확장을 고려한 아이템 시작 (UI에는 반영되지 않지만 재생 시 사용)
  const targetItemStart = targetItem.from - targetExtendedFrames;
  const targetOverlapBeforeStart = Math.max(
    0,
    targetItemStart - transition.from,
  );

  // 에셋의 앞쪽 여유 프레임 수 계산 (assetStart 이전에 재생 가능한 프레임)
  // 논리적 확장이 있으면 해당 확장 프레임만큼 이미 사용 가능
  let targetAssetPrecedingFrames = targetExtendedFrames;
  if ('assetId' in targetItem && targetItem.assetId) {
    const asset = assets[targetItem.assetId];
    if (asset) {
      const assetStart = getAssetStartInSeconds(targetItem) ?? 0;
      const playbackRate = getItemPlaybackRate(targetItem);

      // 에셋의 시작점에서 사용 가능한 프레임 (trimmed 시작점 이전)
      // 이미 targetExtendedFrames를 포함하고 있으므로 추가하지 않음
      targetAssetPrecedingFrames = Math.floor(
        (assetStart * fps) / playbackRate,
      );
    }
  }

  // frozen frames = 트랜지션이 target 시작 이전에 필요한 프레임 - 에셋에서 앞으로 재생 가능한 프레임
  const targetFrozenFrames = Math.max(
    0,
    targetOverlapBeforeStart - targetAssetPrecedingFrames,
  );

  return {
    sourceFrozenFrames: Math.round(sourceFrozenFrames),
    targetFrozenFrames: Math.round(targetFrozenFrames),
  };
};
